// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * NegotiationLayout — top-level Tabs shell for the contract-negotiation
 * reviewer. Each tab body lives in its own file under `./negotiation/`.
 */
import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react'
import {
  AlertTriangle,
  Copy,
  FileText,
  FileWarning,
  LayoutDashboard,
  Mail,
} from 'lucide-react'
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { MarkdownViewer } from '@/components/ui/markdown-viewer'
import type { KbSchema } from '@/subsystems/knowledge-base/shared/types'

import type {
  ContractDuplicateFinding,
  TroublesomeClauseFinding,
} from '../hooks/extractors'
import { ClausesTab } from './negotiation/ClausesTab'
import { DuplicatesTab } from './negotiation/DuplicatesTab'
import { FindingsTab } from './negotiation/FindingsTab'
import { OverviewTab } from './negotiation/OverviewTab'
import { EmptyState, type NegotiationFinding, type NegotiationKbItem } from './negotiation/shared'

export type { NegotiationFinding, NegotiationKbItem }

interface Props {
  findings: NegotiationFinding[]
  kbItems: NegotiationKbItem[]
  /** Schema for the negotiation KB; used to render KB-item modals with section headers. */
  kbSchema?: KbSchema | null
  troublesomeClauses?: TroublesomeClauseFinding[]
  troublesomeKbItems?: NegotiationKbItem[]
  /** Schema for the troublesome-clauses KB. */
  troublesomeKbSchema?: KbSchema | null
  contractDuplicates?: ContractDuplicateFinding[]
  negotiationEmail?: string | null
  /** Open the annotated per-page PDF in a new tab. */
  onOpenPage?: (page: number) => void
  /** Rendered DOCX shown inside the Document tab. */
  documentView?: ReactNode
  /** Controlled tab state — set when parent wants to drive tab switching. */
  tab?: string
  onTabChange?: (tab: string) => void
  /** Show "View in document" affordances on findings/clauses. */
  onOpenInDocument?: (annotationId: string) => void
  /** When set, the Document tab shows a "Back to report" button that returns to this tab. */
  backFromDocumentTo?: string
}

const TAB_TRIGGER_CLASS =
  'gap-1.5 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'

export const NegotiationLayout: FC<Props> = ({
  findings,
  kbItems,
  kbSchema = null,
  troublesomeClauses = [],
  troublesomeKbItems = [],
  troublesomeKbSchema = null,
  contractDuplicates = [],
  negotiationEmail = null,
  onOpenPage,
  documentView,
  tab: controlledTab,
  onTabChange,
  onOpenInDocument,
  backFromDocumentTo,
}) => {
  const [internalTab, setInternalTab] = useState('overview')
  const tab = controlledTab ?? internalTab
  const setTab = useCallback((next: string) => {
    setInternalTab(next)
    onTabChange?.(next)
  }, [onTabChange])

  const rejectCount = useMemo(() => findings.filter(f => f.status === 'reject').length, [findings])
  const fallbackCount = useMemo(() => findings.filter(f => f.status === 'fallback').length, [findings])
  const clauseCount = troublesomeClauses.length
  const duplicateCount = contractDuplicates.length
  const highSeverityDuplicateCount = useMemo(
    () => contractDuplicates.filter(d => d.severity === 'HIGH').length,
    [contractDuplicates],
  )

  if (!kbItems.length && !troublesomeClauses.length && !contractDuplicates.length && !negotiationEmail) {
    return (
      <p className="text-base text-muted-foreground p-6" role="status">
        No knowledge base items configured.
      </p>
    )
  }

  return (
    <Tabs value={tab} onValueChange={setTab} className="w-full h-full flex flex-col overflow-hidden">
      <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 h-auto relative shrink-0">
        <TabsTrigger value="overview" className={TAB_TRIGGER_CLASS}>
          <LayoutDashboard className="h-4 w-4" /> Overview
        </TabsTrigger>
        {documentView && (
          <TabsTrigger value="document" className={TAB_TRIGGER_CLASS}>
            <FileText className="h-4 w-4" /> Document
          </TabsTrigger>
        )}
        <TabsTrigger value="findings" className={TAB_TRIGGER_CLASS}>
          <AlertTriangle className="h-4 w-4 text-amber-500" /> Negotiation
          {(rejectCount + fallbackCount) > 0 && (
            <Badge variant="outline" className="text-xs px-1.5 py-0">{rejectCount + fallbackCount}</Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="clauses" className={TAB_TRIGGER_CLASS}>
          <FileWarning className="h-4 w-4 text-red-500" /> Troublesome Clauses
          {clauseCount > 0 && <Badge variant="outline" className="text-xs px-1.5 py-0">{clauseCount}</Badge>}
        </TabsTrigger>
        <TabsTrigger value="duplicates" className={TAB_TRIGGER_CLASS}>
          <Copy className="h-4 w-4 text-indigo-500" /> Duplicates
          {duplicateCount > 0 && <Badge variant="outline" className="text-xs px-1.5 py-0">{duplicateCount}</Badge>}
        </TabsTrigger>
        <TabsTrigger value="email" className={TAB_TRIGGER_CLASS}>
          <Mail className="h-4 w-4 text-primary" /> Negotiation Email
        </TabsTrigger>
      </TabsList>

      {documentView && (
        <TabsContent value="document" className="mt-0 flex-1 flex flex-col min-h-0">
          {backFromDocumentTo && (
            <div className="shrink-0 px-1 py-2 border-b bg-background">
              <button
                type="button"
                onClick={() => setTab(backFromDocumentTo)}
                className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium hover:bg-accent shadow-sm"
              >
                ← Back to report
              </button>
            </div>
          )}
          <div className="flex-1 min-h-0">
            {documentView}
          </div>
        </TabsContent>
      )}

      <TabsContent value="overview" className="flex-1 min-h-0 overflow-y-auto pt-4">
        <OverviewTab
          rejectCount={rejectCount}
          fallbackCount={fallbackCount}
          clauseCount={clauseCount}
          duplicateCount={duplicateCount}
          highSeverityDuplicateCount={highSeverityDuplicateCount}
          findings={findings}
          troublesomeClauses={troublesomeClauses}
          contractDuplicates={contractDuplicates}
          negotiationEmail={negotiationEmail}
          onOpenEmail={() => setTab('email')}
          onOpenFindings={() => setTab('findings')}
          onOpenClauses={() => setTab('clauses')}
          onOpenDuplicates={() => setTab('duplicates')}
          onOpenPage={onOpenPage}
          onOpenInDocument={onOpenInDocument}
        />
      </TabsContent>

      <TabsContent value="findings" className="flex-1 min-h-0 overflow-y-auto pt-4">
        <FindingsTab
          findings={findings}
          kbItems={kbItems}
          kbSchema={kbSchema}
          onOpenPage={onOpenPage}
          onOpenInDocument={onOpenInDocument}
        />
      </TabsContent>

      <TabsContent value="clauses" className="flex-1 min-h-0 overflow-y-auto pt-4">
        <ClausesTab
          troublesomeClauses={troublesomeClauses}
          troublesomeKbItems={troublesomeKbItems}
          troublesomeKbSchema={troublesomeKbSchema}
          onOpenPage={onOpenPage}
          onOpenInDocument={onOpenInDocument}
        />
      </TabsContent>

      <TabsContent value="duplicates" className="flex-1 min-h-0 overflow-y-auto pt-4">
        <DuplicatesTab duplicates={contractDuplicates} />
      </TabsContent>

      <TabsContent value="email" className="flex-1 min-h-0 overflow-y-auto pt-4">
        {negotiationEmail ? (
          <div className="px-4 py-3 bg-muted/30 rounded border">
            <MarkdownViewer content={negotiationEmail} />
          </div>
        ) : (
          <EmptyState message="No negotiation email has been drafted yet." />
        )}
      </TabsContent>
    </Tabs>
  )
}
